import classnames from "classnames";
import React from "react";
import { useTranslation, Trans } from "react-i18next";

import { useMobile } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import { Text } from "@krea/customer-features/shared-components/text";
import TextActionLink from "@krea/customer-features/shared-components/text/TextActionLink";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { Alert, AlertProps } from "@krea/shared/design-system/alerts/Alert";

interface AlertWithContactPersonProps extends AlertProps {
  innerText: string;
  contactPersonHiddenOnMobile?: boolean;
}

export const AlertWithContactPerson = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & AlertWithContactPersonProps
>(({ variant, className, innerText, contactPersonHiddenOnMobile }, ref) => {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const { data } = useGetCachedCurrentLoanApplication();
  const { assignedToAdminUser: person } = data ?? {};

  const { firstName, mobilePhoneNumber, profileImage } = person || {};
  const phone = mobilePhoneNumber
    ? mobilePhoneNumber
    : settings.contacts.phone.value;
  const phoneLink = mobilePhoneNumber
    ? mobilePhoneNumber.replace(/[^+\d]/g, "")
    : settings.contacts.phone.link;

  return (
    <Alert
      variant={variant}
      className={className}
      rightElement={
        contactPersonHiddenOnMobile && isMobile ? (
          <></>
        ) : (
          <div
            className={classnames(
              "tw-flex tw-max-w-[300px] tw-items-center xl:tw-justify-end",
              className,
            )}
          >
            {profileImage?.lowResolutionUrl && (
              <div className="tw-mr-3 tw-block tw-w-[44px] tw-shrink-0">
                <img
                  src={profileImage.lowResolutionUrl}
                  alt={firstName}
                  className="tw-min-w-100"
                />
              </div>
            )}
            <Text size="md">
              <Trans
                t={t}
                values={{ firstName: firstName, mobilePhoneNumber: phone }}
                components={{
                  a: (
                    <TextActionLink
                      className="no-wrap"
                      value={phone}
                      htmlTag="span"
                      onClick={() => window.open(`tel:${phoneLink}`, "_self")}
                    />
                  ),
                  b: <Text size="md" htmlTag="span" className="tw-font-bold" />,
                }}
              >
                {person && firstName
                  ? t(`commons.support.assignedToAdminUser`)
                  : t(`commons.support.default`)}
              </Trans>
            </Text>
          </div>
        )
      }
    >
      {/* TODO: center vertically in the alert instead of top margin */}
      <p className="tw-mt-5 tw-font-bold">{innerText}</p>
    </Alert>
  );
});
